// Animations

/* eslint-disable wrap-iife */

/* global jQuery */

import { throttle, debounce } from 'throttle-debounce';

import helpers from './helpers';
import setup from './setup';
import gsap from 'gsap';

class DispatchSkinAnimation {

	/**
	 * Constructs the class and sets up instance variables.
	 */
	constructor() {

		this.selector = '.js-animation';
		this.sectionSelector = '.js-section';
		this.resolvedClass = 'is-resolved';
		this.eventNamespace = 'dispatchSkinAnimation';
		this.scrollThrottle = 200;
		this.offsetThreshold = 100;

		this.sections = [];
		this.sectionStagger = 0.35;
		this.sectionChildrenDelay = 0.65;
		this.sectionChildrenStagger = 0.45;

		this.hooks = {
			resolve: [],
			reset: []
		};

		this.windowHeight = window.innerHeight;
	}

	/**
	 * Initializes the class, sets up events for section-based scroll animations.
	 */
	initialize() {

		this.sections = jQuery( this.sectionSelector );

		if ( this.sections.length ) {

			this.setSectionOffsets();
			this.builtInHooks();
			this.events();
		}
	}

	/**
	 * Wires up events for the section scrolling
	 */
	events() {

		const jQueryWindow = jQuery( window );

		// Scroll
		jQueryWindow.on(
			`scroll.${this.eventNamespace}`,
			throttle(
				this.scrollThrottle,
				this.handleScroll.bind( this )
			)
		);

		// Resize
		jQueryWindow.on(
			`resize.${this.eventNamespace}`,
			debounce(
				this.scrollThrottle,
				function() {

					this.setSectionOffsets();
					this.windowHeight = window.innerHeight;
					this.handleScroll();

					// If there are no sections elements left, remove the resize event listener
					if ( ! this.sections.length ) {
						jQueryWindow.off( `resize.${this.eventNamespace}` );
					}
				}.bind( this )
			)
		);

		this.handleScroll();
	}

	/**
	 * Does built-in hooks
	 */
	builtInHooks() {

		this.addHook(
			'.c-section',
			this.resolveSection.bind( this )
		);
	}

	/**
	 * Sets up offsets for sections.
	 */
	setSectionOffsets() {

		for ( let i = 0; i < this.sections.length; i++ ) {

			const section = this.sections[i];
			const jQuerySection = jQuery( section );

			if ( ! section.offsetForScrollAnimation ) {
				section.offsetForScrollAnimation = {};
			}

			// Set offsets
			section.offsetForScrollAnimation.top = jQuerySection.offset().top;
			section.offsetForScrollAnimation.bottom = section.offsetForScrollAnimation.top + section.getBoundingClientRect().height;
		}
	}

	/* Event handlers --------------------------------------- */

	/**
	 * Handles the scroll event
	 */
	handleScroll() {

		// Get scrollTop and scrollBottom
		const scrollTop = window.pageYOffset;
		const scrollBottom = scrollTop + this.windowHeight;
		let resolvedSections = jQuery();

		for ( let i = 0; i < this.sections.length; i++ ) {

			const section = this.sections[i];

			if (
				helpers.doElementOffsetsIntersect(
					section.offsetForScrollAnimation,
					{ top: scrollTop, bottom: scrollBottom },
					this.offsetThreshold
				)
			) {

				resolvedSections = resolvedSections.add( section );
			}
		}

		this.stagger( resolvedSections, this.sectionStagger );
		this.sections = this.sections.not( resolvedSections );

		if ( ! this.sections.length ) {
			jQuery( window ).off( `scroll.${this.eventNamespace}` );
		}
	}

	/* Animation methods ------------------------------------ */

	/**
	 * Resolves an animation.
	 * Adds a class and fires off any hooks that match the element.
	 */
	resolve( element ) {

		element = jQuery( element );

		// If element is an SVG, add the class manually
		if ( element.is( 'svg, line, circle, path' ) ) {

			const currentClass = element.attr( 'class' );
			element.attr( 'class', currentClass + ' ' + this.resolvedClass );
		}

		// Otherwise, just use the J
		else {
			element.addClass( this.resolvedClass );
		}

		this.makeSafariNotSuck( element );

		this.doHooks( element );
	}

	/**
	 * Resets an animation.
	 */
	reset( element ) {

		// If element is an SVG, add the class manually
		if ( element.is( 'svg, line, circle, path' ) ) {

			const currentClass = element.attr( 'class' );
			element.attr( 'class', currentClass.replace( ' ' + this.resolvedClass, '' ) );
		}

		// Otherwise, just use the J
		else {
			element.removeClass( this.resolvedClass );
		}
	}

	/**
	 * Staggers resolution or reset of animations.
	 */
	stagger( elements, stagger = 1, delay = 0, resolve = true ) {

		elements.each(
			function( i, element ) {

				window.setTimeout(
					function() {
						this[resolve ? 'resolve' : 'reset']( jQuery( element ) );
					}.bind( this ),
					delay * 1000 + stagger * i * 1000
				);
			}.bind( this )
		);
	}

	/**
	 * Forces animation to work on safari
	 */
	makeSafariNotSuck( element ) {

		const animationName = element.css( 'animation-name' );
		const tempAnimationName = setup.device === 'safari' ? 'none' : '';

		element.css( 'animation-name', tempAnimationName );

		window.setTimeout(
			function() {
				element.css( 'animation-name', animationName );
			},
			0
		);
	}

	/* Hook methods ----------------------------------------- */

	/**
	 * Adds a hook.
	 */
	addHook( selector, callback, resolve = true ) {

		this.hooks[resolve ? 'resolve' : 'reset'].push(
			{
				selector: selector,
				callback: callback
			}
		);
	}

	/**
	 * Does the hooks.
	 */
	doHooks( element, resolve = true, ...hookArgs ) {

		for ( const hook of this.hooks[resolve ? 'resolve' : 'reset'] ) {

			if ( element.is( hook.selector ) && hook.callback.call( null, element, ...hookArgs ) === false ) {
				return false;
			}
		}

		return true;
	}

	/* Built-in hook callbacks ------------------------------ */

	/**
	 * Section callbacks
	 */
	resolveSection( element ) {

		const masks = element.find( `${this.sectionSelector}__mask` );

		gsap.to(
			masks,
			{ duration: 1.5, ease: 'power3.out', x: 0, onComplete: () => gsap.set( masks, { overflowX: 'visible' } ) },
		);

		this.stagger(
			element.closestDescendant( this.selector, true ),
			this.sectionChildrenStagger,
			this.sectionChildrenDelay
		);
	}
	resetSection( element ) {

		gsap.set(
			element.find( `${this.sectionSelector}__mask` ),
			{ clearProps: 'all' },
		);
		this.stagger( element.closestDescendant( this.selector, true ), 0, 0, false );
	}

}

const animation = new DispatchSkinAnimation();
export default animation;




// WEBPACK FOOTER //
// ./src/js/inc/animation.js